import { useCountUp } from '@/hooks/useCountUp';

interface HeroCardProps {
  /** Top label, e.g. "Today, Shelby handled". */
  label: string;
  /**
   * Numeric value to count up to (e.g. 7). Required when you want the
   * smooth Apple Health-style count-up.
   */
  numericValue: number;
  /** Singular noun (e.g. "call") used to render "1 call" / "0 calls". */
  unit: string;
  /** Sub line, e.g. "4 quotes sent · 1 booking · 1 needs your eyes". */
  sub: string;
  /**
   * Number of conversations needing owner attention. When > 0, the hero
   * leads with a Needs You banner instead of the "today's calls" framing.
   */
  needsAttention?: number;
  /**
   * When true and there's no activity AND no attention, render the
   * "command center" zero state — dark slate hero with system status
   * dots. Keeps the app feeling premium even on a quiet day, instead of
   * staring at "0 calls" (R11 consult 2026-05-06).
   */
  isZeroState?: boolean;
}

/**
 * HeroCard — gradient SS-blue card at the top of the Today view.
 *
 * Three modes:
 *   - Needs you (attention > 0)  → blue card, leads with "X need you now"
 *   - Active (calls > 0)         → blue card, count-up on calls handled
 *   - Zero state (nothing today) → slate-950 "command center" with
 *                                  system status dots
 *
 * Switching to the dark zero-state hero in light mode is intentional —
 * it dodges the "bleak empty white dashboard" feel and frames a quiet
 * day as "Service Steady is online and watching."
 */
export function HeroCard({
  label,
  numericValue,
  unit,
  sub,
  needsAttention = 0,
  isZeroState = false,
}: HeroCardProps) {
  const animated = useCountUp(numericValue, { durationMs: 800 });

  // -- ZERO STATE (true quiet day) --------------------------------------
  if (isZeroState) {
    return (
      <div className="ss-press-light mx-4 mt-4 animate-ss-fade-in rounded-ss-lg bg-slate-950 p-5 text-white shadow-ss-md dark:bg-[#0F172A] dark:ring-1 dark:ring-[#1E293B]">
        <div className="flex items-center gap-2">
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
            Service Steady · Online
          </span>
        </div>
        <div className="mt-3 text-[22px] font-semibold leading-tight tracking-tight">
          Service Steady is watching the phones.
        </div>
        <div className="mt-1.5 text-[13px] leading-snug text-slate-300">
          When a missed call, AI conversation, quote, or booking happens,
          it'll show up here first.
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <SystemDot label="Shelby" status="ok" />
          <SystemDot label="Texts" status="ok" />
          <SystemDot label="Alerts" status="ok" />
        </div>
      </div>
    );
  }

  // -- ATTENTION STATE (X need you now) ---------------------------------
  if (needsAttention > 0) {
    return (
      <div
        className="ss-press-light mx-4 mt-4 animate-ss-fade-in rounded-ss-lg p-5 text-white shadow-ss-md"
        style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)' }}
      >
        <div className="text-xs font-semibold uppercase tracking-wider opacity-85">
          Needs you now
        </div>
        <div className="mt-1 text-[34px] font-bold leading-tight tracking-tight tabular-nums">
          {needsAttention} {needsAttention === 1 ? 'conversation' : 'conversations'}
        </div>
        <div className="text-[13px] opacity-90">{sub}</div>
      </div>
    );
  }

  // -- ACTIVE STATE (calls happened) ------------------------------------
  const display = `${animated} ${animated === 1 ? unit : `${unit}s`}`;
  return (
    <div
      className="ss-press-light mx-4 mt-4 animate-ss-fade-in rounded-ss-lg p-5 text-white shadow-ss-md"
      style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)' }}
    >
      <div className="text-xs font-semibold uppercase tracking-wider opacity-85">
        {label}
      </div>
      <div className="mt-1 text-[34px] font-bold leading-tight tracking-tight tabular-nums">
        {display}
      </div>
      <div className="text-[13px] opacity-90">{sub}</div>
    </div>
  );
}

interface SystemDotProps {
  label: string;
  status: 'ok' | 'warn' | 'down';
}

function SystemDot({ label, status }: SystemDotProps) {
  const dotColor =
    status === 'ok'
      ? 'bg-emerald-400'
      : status === 'warn'
        ? 'bg-amber-400'
        : 'bg-red-400';
  return (
    <div className="flex items-center gap-1.5 rounded-xl bg-white/5 px-2.5 py-1.5 ring-1 ring-white/10">
      <span className={`h-1.5 w-1.5 rounded-full ${dotColor}`} />
      <span className="text-[11px] font-medium text-slate-200">{label}</span>
    </div>
  );
}
